import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

const ProgressHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ProgressBarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const ProgressBarBackground = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '10px',
  backgroundColor: theme.palette.mode === 'dark' ? '#1a202c' : '#eeeeee',
  borderRadius: '5px',
  overflow: 'hidden',
  boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.1), inset -2px -2px 5px rgba(255,255,255,0.1)',
}));

const ProgressBarFill = styled(Box)(({ theme, progress }) => ({
  height: '100%',
  width: `${progress}%`,
  background: 'linear-gradient(to right, #63b3ed, #4299e1)',
  borderRadius: '5px',
}));

const ProgressPercentage = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.125rem',
  color: theme.palette.mode === 'dark' ? '#63b3ed' : '#3182ce',
  minWidth: '40px',
  textAlign: 'right',
}));

const ProgressSection = ({
  userProgress = 0,
  completedLessons = 0,
  totalLessons = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['courses', 'common']);
  
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  const targetProgress = Math.round(userProgress);

  // Анимация прогресса как в примере
  useEffect(() => {
    const animateProgress = (targetPercent, duration = 1500) => {
      let start = null;
      
      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);
        const currentPercent = Math.floor(progress * targetPercent);
        
        setAnimatedProgress(currentPercent);
        setAnimatedPercentage(currentPercent);
        
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      
      requestAnimationFrame(step);
    };

    // Запускаем анимацию при изменении userProgress
    if (targetProgress > 0) {
      animateProgress(targetProgress);
    }
  }, [targetProgress]);

  return (
    <ProgressContainer className="course-progress">
      <ProgressHeader>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="h2"
          sx={{ fontWeight: 600 }}
        >
          {t('courses:courseDetail.yourProgress')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.875rem' }}
        >
          {completedLessons} of {totalLessons} {t('common:stats.lessons')} {t('common:status.completed')}
        </Typography>
      </ProgressHeader>

      <ProgressBarContainer>
        <ProgressBarBackground>
          <ProgressBarFill progress={animatedProgress} />
        </ProgressBarBackground>
        <ProgressPercentage>
          {animatedPercentage}%
        </ProgressPercentage>
      </ProgressBarContainer>
    </ProgressContainer>
  );
};

export default ProgressSection;
